import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { stripLocalePrefix } from "../i18n/localePath";
import { useVersion } from "../version/VersionContext";
import { TITLES_V1, TITLES_V3 } from "../data/seo";
import v1Insights from "../v1/content/insights";

const BRAND = "Parto";

const withBrand = (label) => (label ? `${BRAND} | ${label}` : BRAND);

// Add future static routes here. Detail routes are resolved from the existing
// translated content, so their tab title always matches the page heading.
const staticTitles = {
    de: {
        "/solutions": "Solutions",
        "/infrastructure": "Infrastruktur",
        "/by-role": "Für Ihre Rolle",
        "/company": "Unternehmen",
        "/careers": "Karriere",
        "/partners": "Partner",
        "/ecosystem": "Ökosystem",
        "/contact": "Kontakt",
        "/insights": "Insights",
        "/insights/references": "Referenzen",
        "/insights/case-studies": "Case Studies",
        "/insights/articles": "Fachartikel",
        "/insights/news": "News & Presse",
        "/impressum": "Impressum",
        "/datenschutz": "Datenschutz",
        "/llm-info": "LLM-Informationen",
        "/admin": "Admin",
    },
    en: {
        "/solutions": "Solutions",
        "/infrastructure": "Infrastructure",
        "/by-role": "By Role",
        "/company": "Company",
        "/careers": "Careers",
        "/partners": "Partners",
        "/ecosystem": "Ecosystem",
        "/contact": "Contact",
        "/insights": "Insights",
        "/insights/references": "References",
        "/insights/case-studies": "Case Studies",
        "/insights/articles": "Articles",
        "/insights/news": "News & Press",
        "/impressum": "Imprint",
        "/datenschutz": "Privacy",
        "/llm-info": "LLM Information",
        "/admin": "Admin",
    },
};

const findTitle = (items, slug) => items?.find((item) => item.slug === slug)?.title;

export const getDocumentTitle = (pathname, lang, t, version = "2") => {
    const normalizedPath = stripLocalePrefix(pathname);
    const path = normalizedPath === "/" ? normalizedPath : normalizedPath.replace(/\/$/, "");
    // Version 3 und 4: vollständige Titel aus src/v3/seo.js („Thema | Parto Solution“)
    if (version === "3" || version === "4") {
        const k = lang === "en" ? "en" : "de";
        return TITLES_V3[k]?.[path] || (k === "en" ? "[Page not found] | Parto Solution" : "Seite nicht gefunden | Parto Solution");
    }
    // Version 1: eigene Titel und Beiträge aus src/v1/content/insights.js
    if (version === "1") {
        if (TITLES_V1[lang]?.[path]) return withBrand(TITLES_V1[lang][path]);
        const m = path.match(/^\/insights\/(articles|news)\/([^/]+)$/);
        if (m) return withBrand(findTitle(v1Insights[lang]?.[m[1]], m[2]) || TITLES_V1[lang][`/insights/${m[1]}`]);
    }
    const relaunchTitle = t?.r?.titles?.[path];
    if (relaunchTitle) return withBrand(relaunchTitle);
    const staticTitle = staticTitles[lang]?.[path];
    if (staticTitle) return withBrand(staticTitle);

    const solutionMatch = path.match(/^\/solutions\/([^/]+)$/);
    if (solutionMatch) return withBrand(findTitle(t.solutionsPage?.items, solutionMatch[1]) || staticTitles[lang]["/solutions"]);

    const roleMatch = path.match(/^\/by-role\/([^/]+)$/);
    if (roleMatch) return withBrand(findTitle(t.rolesPage?.roles, roleMatch[1]) || staticTitles[lang]["/by-role"]);

    const careerMatch = path.match(/^\/careers\/([^/]+)$/);
    if (careerMatch) return withBrand(findTitle(t.careersPage?.jobs, careerMatch[1]) || staticTitles[lang]["/careers"]);

    const insightMatch = path.match(/^\/insights\/(case-studies|articles|news)\/([^/]+)$/);
    if (insightMatch) {
        const [, type, slug] = insightMatch;
        const collections = {
            "case-studies": t.insightsPage?.cases,
            articles: t.insightsPage?.articles,
            news: t.insightsPage?.news,
        };
        return withBrand(findTitle(collections[type], slug) || staticTitles[lang][`/insights/${type}`]);
    }

    return BRAND;
};

const DocumentTitleLegacy = () => {
    const { pathname } = useLocation();
    const { lang, t } = useLanguage();
    const { version } = useVersion();

    useEffect(() => {
        // Canonical und Sprachvarianten setzt Seo.jsx (einheitlich mit der festen Domain)
        document.title = getDocumentTitle(pathname, lang, t, version);
    }, [lang, pathname, t, version]);

    return null;
};

export default DocumentTitleLegacy;
